"use client";

import React from "react";
import Image from "next/image";
import s from "../styles";
import Consts from "@common/config/consts";
import { dayjs } from "@lib/client";

/**
 * 指定部品完了表示コンポーネント
 * shitei_complete.ctpをReactに移植した表示専用コンポーネント
 *
 * @param {Object} props - プロパティ
 * @param {Object} props.data - データオブジェクト（partsList, request, answer, partskind等を含む）
 * @param {string} props.quote_number - 見積番号
 * @param {Object} props.photoDialogProps - 写真ダイアログのプロパティ（open関数を含む）
 * @param {Object} props.fileupDialogProps - ファイルアップロードダイアログのプロパティ（open関数を含む）
 * @param {Object} props.henpinDialogProps - 返品値引処理ダイアログのプロパティ（open関数を含む）
 * @param {Object} props.resPict - 写真データのオブジェクト [PAID] => 写真データ
 * @param {Object} props.resFile - ファイルデータのオブジェクト [PAID] => ファイルデータ
 * @param {Object} props.AqbData - 在庫確認データ [PAID] => [total, answered, ...]
 * @returns {JSX.Element} 指定部品完了表示コンポーネント
 */
export default function ShiteiCompleteArea({ data, quote_number, photoDialogProps, fileupDialogProps, henpinDialogProps, resPict = {}, resFile = {}, AqbData = {} }) {
  /**
   * 数値をカンマ区切りでフォーマットする
   * @param {number|string} num - フォーマット対象の数値
   * @returns {string} カンマ区切りの数値文字列
   */
  const formatNumber = (num) => {
    if (!num && num !== 0) return "";
    return Number(num).toLocaleString();
  };

  /**
   * 日付を y/m/d 形式でフォーマットする
   * @param {string} dateString - フォーマット対象の日付文字列
   * @returns {string} フォーマット後の日付文字列（y/m/d形式）、無効な場合はnull
   */
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const d = dayjs(dateString);
    if (!d.isValid()) return null;
    return d.format("YY/MM/DD");
  };

  /**
   * 発送日から現在日までの日数を計算する
   * @param {string} haisoDate - 発送日
   * @returns {number} 日数（発送日が無効な場合はnull）
   * PHPテンプレート: ceil((strtotime(date('Y-m-d')) - strtotime($Sdata['Haiso_Date'])) / (60 * 60 * 24))
   * 日付のみで比較するため、startOf('day')を使用
   */
  const getDaysSinceHaiso = (haisoDate) => {
    if (!haisoDate) return null;
    const haiso = dayjs(haisoDate).startOf("day");
    const today = dayjs().startOf("day");
    if (!haiso.isValid()) return null;
    // PHPテンプレートではceilを使用しているが、diff('day')は既に整数日数を返す
    // ただし、PHPテンプレートでは秒単位で計算してceilしているので、同じ挙動にする
    const diffInMs = today.valueOf() - haiso.valueOf();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
    return Math.ceil(diffInDays);
  };

  /**
   * 返品値引処理ボタンを表示するかどうかを判定する
   * @param {number} hacchuFlg - 発注フラグ
   * @param {string} haisoDate - 発送日
   * @returns {boolean} 表示する場合はtrue
   */
  const shouldShowHenpinButton = (hacchuFlg, haisoDate) => {
    if (hacchuFlg !== Consts.HACCHU_FLG.HASOZUMI && hacchuFlg !== Consts.HACCHU_FLG.NEBIKI) {
      return false;
    }
    const days = getDaysSinceHaiso(haisoDate);
    return days !== null && days < 8;
  };

  /**
   * 返品値引処理列の内容を取得する
   * @param {number} hacchuFlg - 発注フラグ
   * @param {string} haisoDate - 発送日
   * @param {string} paid - 部品回答ID
   * @returns {JSX.Element} 返品値引処理列の内容
   */
  const renderHenpinColumn = (hacchuFlg, haisoDate, paid) => {
    if (hacchuFlg === Consts.HACCHU_FLG.HASOZUMI || hacchuFlg === Consts.HACCHU_FLG.NEBIKI) {
      const showButton = shouldShowHenpinButton(hacchuFlg, haisoDate);
      return (
        <>
          {showButton && (
            <a
              // href="javascript:void(0)"
              className="modal-open3 btn_gray"
              onClick={(e) => {
                e.preventDefault();
                if (henpinDialogProps?.open) {
                  henpinDialogProps.open({
                    mitsumoriNo: quote_number,
                    id: paid,
                  });
                }
              }}
            >
              返品値引処理
            </a>
          )}
          {/* PHPテンプレート: if( $Sdata['Hacchu_flg'] == HACCHU_FLG_NEBIKI ){ echo '値引'; } */}
          {hacchuFlg === Consts.HACCHU_FLG.NEBIKI && "値引"}
        </>
      );
    } else if (hacchuFlg === Consts.HACCHU_FLG.HENPIN) {
      // PHPテンプレート: else if( $Sdata['Hacchu_flg'] == HACCHU_FLG_HENPIN ){ echo '返品処理済'; }
      return <>返品処理済</>;
    }
    return null;
  };

  /**
   * 発注フラグから状態テキストを取得する
   * @param {number} hacchuFlg - 発注フラグ
   * @param {boolean} isBulkEstimate - 一括見積セクションかどうか（true: nonegr, false: none）
   * @returns {Object} { jyotai: string, bgcolor: string } 状態テキストと背景色クラス
   */
  const getHacchuStatus = (hacchuFlg, isBulkEstimate = false) => {
    if (!hacchuFlg && hacchuFlg !== 0) {
      return { jyotai: "", bgcolor: "" };
    }

    const bgcolorClass = isBulkEstimate ? "nonegr" : "none";

    switch (hacchuFlg) {
      case Consts.HACCHU_FLG.HASOZUMI:
        return { jyotai: "発送済", bgcolor: "" };
      case Consts.HACCHU_FLG.KAKUTEI:
        return { jyotai: "注文確定", bgcolor: "" };
      case Consts.HACCHU_FLG.NEBIKI:
        return { jyotai: "値引", bgcolor: "" };
      case Consts.HACCHU_FLG.HENPIN:
        return { jyotai: "返品", bgcolor: "" };
      case Consts.HACCHU_FLG.ZAIKONASHI:
        return { jyotai: "在庫なし回答", bgcolor: bgcolorClass };
      case Consts.HACCHU_FLG.KIGENGIRE:
        return { jyotai: "在庫回答時間切れ", bgcolor: bgcolorClass };
      default:
        return { jyotai: "", bgcolor: "" };
    }
  };

  /**
   * 発送可能日の値を表示テキストに変換する
   * @param {number} hasso - 発送可能日の値
   * @returns {string} 表示テキスト
   */
  const getHassoStatusText = (hasso) => {
    if (!hasso && hasso !== 0) return "";
    const status = Consts.SELECTION.HASSO_STATUS.find((item) => item.key === hasso);
    return status ? status.value : "";
  };

  /**
   * 発送区分の表示テキストを取得する
   * @param {number} haisoKubun - 発送区分
   * @returns {string} 表示テキスト
   */
  const getHaisoKubunText = (haisoKubun) => {
    if (!haisoKubun && haisoKubun !== 0) return "";
    const status = Consts.SELECTION.HAISO_KUBUN.find((item) => item.key === haisoKubun);
    return status ? status.value : "";
  };

  /**
   * 部品カテゴリのクラス名を取得する
   * @param {number} pkid - 部品カテゴリID
   * @returns {string} クラス名（icon_r, icon_rb, icon_g, icon_e, icon_y）
   */
  const getCategoryClassName = (pkid) => {
    const categoryMap = {
      1: "icon_r",
      2: "icon_rb",
      3: "icon_g",
      4: "icon_e",
      5: "icon_y",
    };
    return categoryMap[pkid] || "";
  };

  /**
   * 部品カテゴリ名を取得する
   * @param {number} pkid - 部品カテゴリID
   * @param {Array} partskind - 部品カテゴリマスタ配列
   * @returns {string} 部品カテゴリ名
   */
  const getCategoryName = (pkid, partskind) => {
    if (!pkid || !partskind) return "";
    const category = partskind.find((cat) => cat?.Partskind?.PartsKindID === pkid);
    return category?.Partskind?.PartsKindName || "";
  };

  /**
   * 在庫確認ボタンの画像名を取得する
   * @param {string} paid - 部品回答ID
   * @param {string} requestLimit - 依頼期限
   * @returns {string} ボタン画像名（空文字列の場合は表示しない）
   */
  const getQabButtonName = (paid, requestLimit) => {
    if (!requestLimit) {
      return "";
    }

    // 回答を行えるのは見積期限+1週間まで
    const deadline = dayjs(requestLimit).add(1, "week");
    if (!dayjs().isBefore(deadline)) {
      return "";
    }

    const aqbData = AqbData[paid];
    if (!aqbData) {
      return "";
    } else if (!aqbData[2]) {
      return "qa_btn4";
    } else if (aqbData[2] >= aqbData[1]) {
      return "qa_btn5";
    } else {
      return "qa_btn4";
    }
  };

  // 現車と紐付いている場合は色変える
  const isGensha = data?.answer?.[0]?.RequestAnswer?.GenshaID ? true : false;

  // 見積期限が現在時刻以降かどうか
  // PHPテンプレート: strtotime($request['OrderedRequest']['Request_Limit']) >= strtotime(date('Y-m-d H:i:s'))
  // >= は時刻も含むため、isAfter または isSame（時刻を含む）を使用
  const isRequestLimitValid = data?.request?.OrderedRequest?.Request_Limit && !dayjs(data.request.OrderedRequest.Request_Limit).isBefore(dayjs());

  // 依頼期限の判定
  // PHPテンプレート: strtotime($request['OrderedRequest']['Request_Limit']) >= strtotime(Consts::CARRY_TAXIN_DATE)
  const isAfterCarryTaxinDate =
    data?.request?.OrderedRequest?.Request_Limit &&
    (dayjs(data.request.OrderedRequest.Request_Limit).isAfter(dayjs(Consts.CARRY_TAXIN_DATE)) || dayjs(data.request.OrderedRequest.Request_Limit).isSame(dayjs(Consts.CARRY_TAXIN_DATE), "day"));

  if (!data || !data.partsList || data.partsList.length === 0) {
    return null;
  }

  // 部品取り一括見積セクション用のデータ
  const firstParts = data.partsList[0];
  const sData = firstParts?.PartsAnswer;
  const paid = sData?.id;
  // PHPテンプレートでは、一括見積セクションでは見積期限+1週間のチェックを行わず、直接AqbDataをチェック
  // PHPテンプレート: if(empty($AqbData[$paid])){ $bt_name_qab = ''; }elseif(empty($AqbData[$paid][2])){ $bt_name_qab = 'qa_btn4'; }elseif($AqbData[$paid][2] >= $AqbData[$paid][1]){ $bt_name_qab = 'qa_btn5'; }else{ $bt_name_qab = 'qa_btn4'; }
  let btNameQab = "";
  if (paid) {
    const aqbData = AqbData[paid];
    if (!aqbData) {
      btNameQab = "";
    } else if (!aqbData[2]) {
      btNameQab = "qa_btn4";
    } else if (aqbData[2] >= aqbData[1]) {
      btNameQab = "qa_btn5";
    } else {
      btNameQab = "qa_btn4";
    }
  }
  const hacchuFlg = sData?.Hacchu_flg;
  // PHPテンプレート: 一括見積セクションでは bgcolor = "nonegr"
  const { jyotai, bgcolor } = getHacchuStatus(hacchuFlg, true);

  // 写真アイコンの判定
  // PHPテンプレート: if(empty($resPict[$Sdata['id']])){ $icon_name = "camera_icon.png"; }else{ $icon_name = "camera_icon2.png"; }
  const iconName = resPict[paid] ? "camera_icon2.png" : "camera_icon.png";

  // 一括見積セクションの発送情報表示関数
  // PHPテンプレート: 発送方法は既存データのみ表示、運送会社・伝票番号は条件付き入力フォーム、発送日は既存データのみ表示
  const renderBulkShippingInfo = () => {
    // PHPテンプレート: if(empty($bgcolor)){ $bgcolor = "#DCDCDC"; }
    const bgColorValue = !bgcolor || bgcolor === "none" || bgcolor === "nonegr" ? "#DCDCDC" : bgcolor;

    // 発送方法: 既存データのみ表示
    const haisoKubunCell = <td style={{ backgroundColor: bgColorValue }}>{getHaisoKubunText(sData?.Haiso_kubun)}</td>;

    // 運送会社・伝票番号: 条件付き入力フォーム
    // PHPテンプレート: if($Sdata['Haiso_kubun'] != 1 or ($Sdata['Haiso_kubun'] == 1 and $Sdata['Haiso_Corp'])){ ... }else{ ... }
    const haisoCorpCell =
      sData?.Haiso_kubun !== Consts.HAISO_KUBUN.HASSO || (sData?.Haiso_kubun === Consts.HAISO_KUBUN.HASSO && sData?.Haiso_Corp) ? (
        <td style={{ backgroundColor: bgColorValue }}>
          {sData?.Haiso_Corp || ""}
          {sData?.Haiso_Corp && sData?.Haiso_denpyo && <br />}
          {sData?.Haiso_denpyo || ""}
        </td>
      ) : (
        <td align="left" style={{ backgroundColor: "#e5ffe5" }}>
          <input type="text" name="data[Answer][0][haiso_corp]" id="haiso_corp_0" maxLength={60} placeholder="例）西濃運輸" />
          <br />
          <input type="text" name="data[Answer][0][haiso_denpyo]" id="haiso_denpyo_0" maxLength={60} style={{ width: "8em" }} placeholder="例）12345678" />
        </td>
      );

    // 発送日: 既存データのみ表示
    const haisoYoteiCell = <td style={{ backgroundColor: bgColorValue }}>{sData?.Haiso_Yotei ? formatDate(sData.Haiso_Yotei) : ""}</td>;

    return (
      <>
        {haisoKubunCell}
        {haisoCorpCell}
        {haisoYoteiCell}
      </>
    );
  };

  return (
    <>
      {/* ヘッダーセクション */}
      <s.PartsTitle>
        部品情報
        {isRequestLimitValid && (
          <>
            {/* 質問ボタン (1番目の部品の部品コードを→　部品ごとに分けるようなら空に) */}
            <s.QuestionButton className="important-btn2 qaOpen2" style={{ verticalAlign: "middle", marginTop: "-5px" }} data-key={data.partsList?.[0]?.PartsRequest?.id || ""}>
              依頼元へ質問を行う | 回答を見る
            </s.QuestionButton>
            {isGensha && (
              <s.StockedVehicleButton style={{ verticalAlign: "middle", marginTop: "-5px" }} className="important-btn4">
                <Image src="/images/gensha_icon_w.png" alt="入庫済み車輌" width={20} height={20} />
                入庫済みの車輌があります
              </s.StockedVehicleButton>
            )}
          </>
        )}
      </s.PartsTitle>

      {/* 最終回答者表示 */}
      {data?.answer?.[0]?.CA?.UserName && (
        <div style={{ marginTop: "10px" }}>
          <span style={{ backgroundColor: "#ff3333", fontSize: "16px", color: "#ffffff" }}>　最終回答者： {data.answer[0].CA.UserName} 様&nbsp;&nbsp;&nbsp;</span>
        </div>
      )}

      {/* 部品取り一括見積セクション */}
      <s.PartsConfirmTable>
        <tbody id="parts0">
          <tr>
            <th colSpan="10" className="title buhin" scope="row">
              部品取り一括見積
              <span style={{ float: "right" }}>
                {jyotai}
                <br />
                {btNameQab && (
                  <span className="qabOpen" data-key={`${data?.answer?.[0]?.RequestAnswer?.id}/${paid}`} style={{ cursor: "pointer" }}>
                    <Image src={`/images/${btNameQab}.png`} alt="在庫確認" width={20} height={20} />
                  </span>
                )}
              </span>
              <p>(依頼者備考：{data?.request?.OrderedRequest?.Bikou || ""})</p>
            </th>
          </tr>
          <tr>
            <th>一括見積備考</th>
            <th>価格(税別)</th>
            <th>送料(税別)</th>
            <th>発送可能日</th>
            <th>見積有効期限</th>
            <th>写真</th>
            <th>発送方法</th>
            <th>運送会社・伝票番号</th>
            <th>発送日</th>
            <th>返品値引処理</th>
          </tr>
          <tr className={bgcolor || undefined}>
            <td style={{ whiteSpace: "pre-line", textAlign: "left" }}>
              {sData?.Bikou
                ? sData.Bikou.split("\n").map((line, idx) => (
                    <React.Fragment key={idx}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))
                : ""}
            </td>
            {sData?.StanderdPrice === -99 ? (
              <>
                <td>在庫なし</td>
                <td></td>
              </>
            ) : (
              <>
                <td>{sData?.StanderdPrice ? `${formatNumber(sData.StanderdPrice)}円` : ""}</td>
                <td>{sData?.Soryo ? `${formatNumber(sData.Soryo)}円` : ""}</td>
              </>
            )}
            <td>{getHassoStatusText(sData?.Hasso)}</td>
            <td>{sData?.Sell_Limit ? formatDate(sData.Sell_Limit) : null}</td>
            <td>
              <a
                href={`/ordereddetail/photo/${quote_number}/0/1/${Consts.WRECKER_PHOTO.KARI_REF}`}
                rel="internal"
                onClick={(e) => {
                  e.preventDefault();
                  if (photoDialogProps?.open) {
                    photoDialogProps.open({
                      mitsumoriNo: quote_number,
                      prid: 0,
                      seqnum: 1,
                      refMode: Consts.WRECKER_PHOTO.KARI_REF,
                    });
                  }
                }}
              >
                <Image src={`/images/${iconName}`} alt="写真" width={16} height={16} />
              </a>
              <br />
            </td>
            {renderBulkShippingInfo()}
            <td>{renderHenpinColumn(hacchuFlg, sData?.Haiso_Date, paid)}</td>
          </tr>
        </tbody>
      </s.PartsConfirmTable>

      {/* 追加商品情報セクション */}
      <h3>追加商品情報</h3>
      <s.PartsConfirmTable>
        {(() => {
          const rows = [];
          const partsList = data.partsList || [];

          for (let i = 1; i < partsList.length; i++) {
            const partsItem = partsList[i];
            const PA_PRID = partsItem?.PartsAnswer?.PRID;
            const PA_PAID = partsItem?.PartsAnswer?.id;

            // PA_PRID == 0 かつ StanderdPrice が空の場合はスキップ
            // PHPテンプレート: if( $PA_PRID == 0 && empty( $partsList[$i]['PartsAnswer']['StanderdPrice'] ) ){ continue; }
            if (PA_PRID === 0 && !partsItem?.PartsAnswer?.StanderdPrice) {
              continue;
            }

            // PHPテンプレート: $display = 0;
            let display = 0;
            if (partsItem?.PartsAnswer?.StanderdPrice) {
              display = 1;
            }

            // 回答を行えるのは見積期限+1週間まで
            const requestLimit = data?.request?.OrderedRequest?.Request_Limit;
            const paidForQab = display === 1 ? partsItem.PartsAnswer.id : null;
            const btNameQab = display === 1 && requestLimit ? getQabButtonName(paidForQab, requestLimit) : "";
            const hacchuFlg = display === 1 ? partsItem.PartsAnswer.Hacchu_flg : null;
            // PHPテンプレート: 追加商品情報セクションでは bgcolor = "none"
            const { jyotai: jyotaiItem, bgcolor: bgcolorItem } = display === 1 ? getHacchuStatus(hacchuFlg, false) : { jyotai: "", bgcolor: "" };

            // 部品名ヘッダー行（displayの値に関係なく表示）
            rows.push(
              <React.Fragment key={`header-${i}`}>
                <tr>
                  <th colSpan="16" className="title" scope="row" style={{ borderRightStyle: "none" }}>
                    {PA_PRID === 0 ? (
                      <>
                        {partsItem?.PartsAnswer?.OthPartsName || ""}
                        {partsItem?.PartsAnswer?.OthPartsSonota && <>　品名：{partsItem.PartsAnswer.OthPartsSonota}</>}
                      </>
                    ) : (
                      <>
                        {partsItem?.PartsRequest?.P_PartsName}&nbsp;
                        {(() => {
                          const partskind = data.partskind || [];
                          let pk3flg = 0;
                          const pkids = [
                            partsItem?.PartsRequest?.P_PKID1,
                            partsItem?.PartsRequest?.P_PKID2,
                            partsItem?.PartsRequest?.P_PKID3,
                            partsItem?.PartsRequest?.P_PKID4,
                            partsItem?.PartsRequest?.P_PKID5,
                          ];

                          return (
                            <>
                              {pkids.map((pkid, idx) => {
                                if (!pkid || pkid === 3) {
                                  if (pkid === 3) pk3flg = 1;
                                  return null;
                                }
                                const categoryName = getCategoryName(pkid, partskind);
                                const className = getCategoryClassName(pkid);
                                return (
                                  <s.PartsCategoryIcon key={idx} className={className}>
                                    {categoryName}
                                  </s.PartsCategoryIcon>
                                );
                              })}
                              {pk3flg === 1 && (
                                <>
                                  <span>　　</span>
                                  <s.PartsCategoryIcon className="icon_g">{getCategoryName(3, partskind)}</s.PartsCategoryIcon>
                                </>
                              )}
                            </>
                          );
                        })()}
                        {partsItem?.PartsRequest?.Bikou == null ? <p></p> : <p>({partsItem.PartsRequest.Bikou})</p>}
                      </>
                    )}
                  </th>
                </tr>
                <tr>
                  <th>&nbsp;</th>
                  <th scope="row">純正品番</th>
                  <th>数量</th>
                  <th>部品カテゴリ</th>
                  <th>価格(税別)</th>
                  {isAfterCarryTaxinDate ? (
                    <th style={{ backgroundColor: "#FFF666" }}>送料(税別)</th>
                  ) : (
                    <th>
                      送料<span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                    </th>
                  )}
                  <th>発送可能日</th>
                  <th>カラー番号</th>
                  <th>カラー名称</th>
                  <th>写真</th>
                  <th>部品備考</th>
                  <th scope="row">自社管理番号</th>
                  <th>発送方法</th>
                  <th>運送会社・伝票番号</th>
                  <th>発送日</th>
                  <th>返品値引処理</th>
                </tr>

                {/* 回答データ行（display === 1の場合のみ表示） */}
                {display === 1 && (
                  <tr key={`data-${i}`} className={bgcolorItem || undefined}>
                    <td>
                      {jyotaiItem}
                      <br />
                      {btNameQab && (
                        <img
                          src={`/images/${btNameQab}.png`}
                          className="qabOpen btn_gray"
                          data-key={`${data.answer?.[0]?.RequestAnswer?.id}/${paidForQab}`}
                          style={{ cursor: "pointer" }}
                          alt="在庫確認"
                        />
                      )}
                    </td>
                    <td scope="row">{partsItem?.PartsAnswer?.PartsJyunseiCode || ""}</td>
                    <td>{partsItem?.PartsAnswer?.Suryo || ""}</td>
                    <td>{getCategoryName(partsItem?.PartsAnswer?.PartsKindID, data.partskind)}</td>
                    <td>
                      {(() => {
                        // PHPテンプレートでは partsList[$i][0]['PartsPrice'] を使用
                        // partsList[i]が配列で最初の要素にPartsPriceがある場合、または直接PartsAnswerにStanderdPriceがある場合を考慮
                        let partsPrice = "";
                        if (Array.isArray(partsItem) && partsItem[0]) {
                          partsPrice = partsItem[0]?.PartsPrice || "";
                        } else if (partsItem?.PartsAnswer?.StanderdPrice) {
                          partsPrice = partsItem.PartsAnswer.StanderdPrice;
                        } else if (partsItem?.PartsAnswer?.Price) {
                          partsPrice = partsItem.PartsAnswer.Price;
                        }
                        return partsPrice ? `${formatNumber(partsPrice)}円` : "";
                      })()}
                    </td>
                    <td>
                      {(() => {
                        // PHPテンプレートでは partsList[$i][0]['Soryo'] を使用
                        // partsList[i]が配列で最初の要素にSoryoがある場合、または直接PartsAnswerにSoryo/DeliveryPriceがある場合を考慮
                        // PHPテンプレート: ($partsList[$i][0]['Soryo'] == 0 and $partsList[$i]['PartsAnswer']['Hacchu_flg'] != HACCHU_FLG_HENPIN) ? "送料込" : number_format( $partsList[$i][0]['Soryo'] )."円"
                        let soryo = 0;
                        if (Array.isArray(partsItem) && partsItem[0]) {
                          soryo = partsItem[0]?.Soryo !== undefined ? partsItem[0].Soryo : 0;
                        } else if (partsItem?.PartsAnswer?.Soryo !== undefined) {
                          soryo = partsItem.PartsAnswer.Soryo;
                        } else if (partsItem?.PartsAnswer?.DeliveryPrice !== undefined) {
                          soryo = partsItem.PartsAnswer.DeliveryPrice;
                        }
                        // Hacchu_flg == HENPIN の場合は送料が0でも「送料込」ではなく数値表示
                        // PHPテンプレート: ($partsList[$i][0]['Soryo'] == 0 and $partsList[$i]['PartsAnswer']['Hacchu_flg'] != HACCHU_FLG_HENPIN) ? "送料込" : number_format( $partsList[$i][0]['Soryo'] )."円"
                        if (soryo === 0 && hacchuFlg !== Consts.HACCHU_FLG.HENPIN) {
                          return "送料込";
                        }
                        // 送料が0でもHENPINの場合は「0円」を表示
                        return `${formatNumber(soryo)}円`;
                      })()}
                    </td>
                    <td>{getHassoStatusText(partsItem?.PartsAnswer?.Hasso)}</td>
                    <td>{partsItem?.PartsAnswer?.ColorNo || ""}</td>
                    <td>{partsItem?.PartsAnswer?.ColorName || ""}</td>
                    <td>
                      {(() => {
                        const mitsumoriNo = quote_number;
                        const seqnum = partsItem?.PartsAnswer?.Seqnum;
                        const PRID = partsItem?.PartsRequest?.id;
                        const othPName = partsItem?.PartsAnswer?.OthPartsName;

                        // PHPテンプレートでは !empty($resPict[$PA_PAID]) でチェック
                        const hasPict = resPict[PA_PAID] !== undefined && resPict[PA_PAID] !== null && resPict[PA_PAID] !== "";
                        const hasFile = resFile[PA_PAID] !== undefined && resFile[PA_PAID] !== null && resFile[PA_PAID] !== "";

                        if (PA_PRID === 0) {
                          return (
                            <>
                              {hasPict && (
                                <a
                                  href={`/ordereddetail/photo/${mitsumoriNo}/0/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}/${othPName}`}
                                  rel="internal"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (photoDialogProps?.open) {
                                      photoDialogProps.open({
                                        mitsumoriNo: mitsumoriNo,
                                        prid: 0,
                                        seqnum: seqnum,
                                        refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                        othPName: othPName,
                                      });
                                    }
                                  }}
                                >
                                  <Image src="/images/camera_icon2.png" alt="写真" width={16} height={16} />
                                </a>
                              )}
                              {hasPict && <br />}
                              {hasFile && (
                                <a
                                  href={`/ordereddetail/fileup/${mitsumoriNo}/0/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}/${othPName}`}
                                  rel="internal"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (fileupDialogProps?.open) {
                                      fileupDialogProps.open({
                                        mitsumoriNo: mitsumoriNo,
                                        prid: 0,
                                        seqnum: seqnum,
                                        refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                        othPName: othPName,
                                      });
                                    }
                                  }}
                                  style={{ cursor: "pointer", padding: "5px 0px 0px" }}
                                >
                                  <Image src="/images/file_icon.png" alt="ファイル" width={16} height={16} />
                                </a>
                              )}
                            </>
                          );
                        } else {
                          return (
                            <>
                              {hasPict && (
                                <a
                                  href={`/ordereddetail/photo/${mitsumoriNo}/${PRID}/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}`}
                                  rel="internal"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (photoDialogProps?.open) {
                                      photoDialogProps.open({
                                        mitsumoriNo: mitsumoriNo,
                                        prid: PRID,
                                        seqnum: seqnum,
                                        refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                      });
                                    }
                                  }}
                                >
                                  <Image src="/images/camera_icon2.png" alt="写真" width={16} height={16} />
                                </a>
                              )}
                              {hasPict && <br />}
                              {hasFile && (
                                <a
                                  href={`/ordereddetail/fileup/${mitsumoriNo}/${PRID}/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}`}
                                  rel="internal"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (fileupDialogProps?.open) {
                                      fileupDialogProps.open({
                                        mitsumoriNo: mitsumoriNo,
                                        prid: PRID,
                                        seqnum: seqnum,
                                        refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                      });
                                    }
                                  }}
                                  style={{ cursor: "pointer", padding: "5px 0px 0px" }}
                                >
                                  <Image src="/images/file_icon.png" alt="ファイル" width={16} height={16} />
                                </a>
                              )}
                            </>
                          );
                        }
                      })()}
                    </td>
                    <td style={{ whiteSpace: "pre-line", textAlign: "left" }}>
                      {partsItem?.PartsAnswer?.Bikou
                        ? partsItem.PartsAnswer.Bikou.split("\n").map((line, idx) => (
                            <React.Fragment key={idx}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))
                        : ""}
                    </td>
                    <td scope="row" style={!bgcolorItem ? { backgroundColor: "#FDF5E6" } : undefined}>
                      {partsItem?.PartsAnswer?.JishaNo || ""}
                      <br />
                      {partsItem?.PartsAnswer?.PrepKind === Consts.GOODS.INHOUSE && <label>自社品</label>}
                      {partsItem?.PartsAnswer?.PrepKind === Consts.GOODS.OTHER && <label>他社品</label>}
                    </td>
                    {/* 発送情報列 */}
                    {(() => {
                      // PHPテンプレート: if(empty($bgcolor)){ $bgcolor = "#DCDCDC"; }
                      const bgColorValue = !bgcolorItem || bgcolorItem === "none" || bgcolorItem === "nonegr" ? "#DCDCDC" : bgcolorItem;

                      // 発送方法: 既存データのみ表示
                      const haisoKubunCell = <td style={{ backgroundColor: bgColorValue }}>{getHaisoKubunText(partsItem?.PartsAnswer?.Haiso_kubun)}</td>;

                      // 運送会社・伝票番号: 条件付き入力フォーム
                      // PHPテンプレート: if($partsList[$i]['PartsAnswer']['Haiso_kubun'] != 1 or ($partsList[$i]['PartsAnswer']['Haiso_kubun'] == 1 and $partsList[$i]['PartsAnswer']['Haiso_Corp'])){ ... }else{ ... }
                      const haisoCorpCell =
                        partsItem?.PartsAnswer?.Haiso_kubun !== Consts.HAISO_KUBUN.HASSO || (partsItem?.PartsAnswer?.Haiso_kubun === Consts.HAISO_KUBUN.HASSO && partsItem?.PartsAnswer?.Haiso_Corp) ? (
                          <td style={{ backgroundColor: bgColorValue }}>
                            {partsItem?.PartsAnswer?.Haiso_Corp || ""}
                            {partsItem?.PartsAnswer?.Haiso_Corp && partsItem?.PartsAnswer?.Haiso_denpyo && <br />}
                            {partsItem?.PartsAnswer?.Haiso_denpyo || ""}
                          </td>
                        ) : (
                          <td align="left" style={{ backgroundColor: "#e5ffe5" }}>
                            <input type="text" name={`data[Answer][${i}][haiso_corp]`} id={`haiso_corp_${i}`} maxLength={60} placeholder="例）西濃運輸" />
                            <br />
                            <input type="text" name={`data[Answer][${i}][haiso_denpyo]`} id={`haiso_denpyo_${i}`} maxLength={60} style={{ width: "8em" }} placeholder="例）12345678" />
                          </td>
                        );

                      // 発送日: 既存データのみ表示
                      const haisoYoteiCell = <td style={{ backgroundColor: bgColorValue }}>{partsItem?.PartsAnswer?.Haiso_Yotei ? formatDate(partsItem.PartsAnswer.Haiso_Yotei) : ""}</td>;

                      return (
                        <>
                          {haisoKubunCell}
                          {haisoCorpCell}
                          {haisoYoteiCell}
                        </>
                      );
                    })()}
                    {/* 返品値引処理列 */}
                    <td>{renderHenpinColumn(hacchuFlg, partsItem?.PartsAnswer?.Haiso_Date, PA_PAID)}</td>
                  </tr>
                )}
              </React.Fragment>
            );

            // グループの終了判定
            // PHPテンプレート: if( ($i + 1) >= count($partsList) || ($PA_PRID == 0 && $partsList[$i]['PartsAnswer']['OthPartsName'] != $partsList[$i+1]['PartsAnswer']['OthPartsName']) || ($PA_PRID != $partsList[$i+1]['PartsAnswer']['PRID']) )
            const nextItem = partsList[i + 1];
            if (
              i + 1 >= partsList.length ||
              (PA_PRID === 0 && partsItem?.PartsAnswer?.OthPartsName !== nextItem?.PartsAnswer?.OthPartsName) ||
              (PA_PRID !== 0 && nextItem && PA_PRID !== nextItem?.PartsAnswer?.PRID)
            ) {
              // 未回答行の表示（PHPテンプレートでは16列）
              // PHPテンプレート: if( $display == 0 && $PA_PRID != 0 )
              if (display === 0 && PA_PRID !== 0) {
                rows.push(
                  <tr key={`none-${i}`} className="none">
                    <td scope="row">-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td scope="row">-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                );
              }
              // break はコメントアウトされているため、処理は続行
            }
          }

          return rows;
        })()}
      </s.PartsConfirmTable>
    </>
  );
}
